import axios, { CancelTokenSource } from 'axios';
import Api from 'services/api';

const SEARCH_TIMEOUT_MS = 100000;
const ANALYZE_TIMEOUT_MS = 35000;
const IMPORT_TIMEOUT_MS = 105000;

export const ANALISE_QUERY_MAX = 500;

export const REGIOES: Record<string, string[]> = {
  Norte: ['AC', 'AP', 'AM', 'PA', 'RO', 'RR', 'TO'],
  Nordeste: ['AL', 'BA', 'CE', 'MA', 'PB', 'PE', 'PI', 'RN', 'SE'],
  'Centro-Oeste': ['DF', 'GO', 'MT', 'MS'],
  Sudeste: ['ES', 'MG', 'RJ', 'SP'],
  Sul: ['PR', 'RS', 'SC'],
};

export const UFS: string[] = Object.keys(REGIOES).reduce(
  (acc: string[], regiao) => acc.concat(REGIOES[regiao]),
  [],
);

export const regiaoDaUf = (uf: string): string | null => {
  const alvo = uf.toUpperCase();
  const regioes = Object.keys(REGIOES);
  for (let i = 0; i < regioes.length; i += 1) {
    if (REGIOES[regioes[i]].indexOf(alvo) >= 0) return regioes[i];
  }
  return null;
};

export const regiaoUnicaDe = (ufs: string[]): string | null => {
  if (!ufs.length) return null;
  const regioes = ufs.map(regiaoDaUf).filter((r): r is string => Boolean(r));
  return regioes.length && regioes.every((r) => r === regioes[0]) ? regioes[0] : null;
};

const UNIDADE_LABELS: Record<string, string> = {
  'M²': 'm²',
  'M³': 'm³',
  METRO: 'm',
  KM: 'km',
  KG: 'kg',
  TON: 'ton',
  L: 'litro',
  HORA: 'hora',
  DIA: 'dia',
  MÊS: 'mês',
};

const ALIASES_UNIDADE: Record<string, string> = {};

const adicionarAliases = (canonica: string, aliases: string[]) =>
  aliases.forEach((a) => {
    ALIASES_UNIDADE[a] = canonica;
  });

adicionarAliases('UN', ['un', 'und', 'unid', 'unidade', 'unidades']);
adicionarAliases('METRO', ['m', 'mt', 'mts', 'metro', 'metros', 'metrolinear', 'metro linear']);
adicionarAliases('KM', ['km', 'kms', 'quilometro', 'quilometros', 'kilometro', 'kilometros']);
adicionarAliases('M²', ['m2', 'm²', 'mq', 'metroquadrado', 'metro quadrado']);
adicionarAliases('M³', ['m3', 'm³', 'metrocubico', 'metro cubico']);
adicionarAliases('KG', ['kg', 'quilo', 'quilos', 'quilograma', 'quilogramas', 'kilo']);
adicionarAliases('G', ['g', 'gr', 'grama', 'gramas']);
adicionarAliases('TON', ['ton', 't', 'tn', 'tonelada', 'toneladas']);
adicionarAliases('L', ['l', 'lt', 'lts', 'litro', 'litros']);
adicionarAliases('ML', ['ml', 'mililitro', 'mililitros']);
adicionarAliases('MÊS', ['mes', 'mês', 'meses', 'mensal']);
adicionarAliases('HORA', ['h', 'hr', 'hora', 'horas']);
adicionarAliases('DIA', ['dia', 'dias', 'diaria', 'diária']);

export const unidadeCanonica = (u?: string | null): string | null => {
  if (!u || !u.trim()) return null;
  const chave = u.trim().toLowerCase().replace(/\s+/g, ' ');
  return (
    ALIASES_UNIDADE[chave] || ALIASES_UNIDADE[chave.replace(/\./g, '')] || u.trim().toUpperCase()
  );
};

export const unidadeLabel = (u?: string | null): string => {
  if (!u) return '';
  return UNIDADE_LABELS[u] || u.toLowerCase();
};

export type TipoQuery = 'produto' | 'servico' | 'obra';

export interface ClarificacaoPergunta {
  id: string;
  pergunta: string;
  tipo: 'escolha' | 'quantidade' | 'texto';
  opcoes?: string[] | null;
  unidade?: string | null;
  refina_busca: boolean;
}

export interface AnalisarResponse {
  tipo: TipoQuery;
  spec_assumida: string | null;
  queries: string[];
  unidade_fisica_tipica: string | null;
  clarificacao: {
    necessaria: boolean;
    motivo: string | null;
    perguntas: ClarificacaoPergunta[];
  };
}

export interface SearchHitPayload {
  item_descricao?: string;
  item_descricao_detalhado?: string;
  item_numero?: string;
  item_valor_unitario?: number;
  item_valor_total?: number;
  item_unidade?: string;
  item_quantidade?: number;
  uf?: string;
  municipio?: string;
  orgao_id?: number;
  orgao_nome?: string;
  fornecedor?: string;
  cnpj?: string;
  marca?: string;
  fabricante?: string;
  modelo?: string;
  created?: string;
  data_validade_licitacao?: string;
  objeto?: string;
  id_original?: number;
  portal_id?: number;
  licitacao_id?: number;
  codigo_site?: string;
}

export interface SearchHit {
  id: string | number;
  score: number;
  tipo_operacao?: string;
  item_unidade_norm?: string;
  payload: SearchHitPayload;
  preco_normalizado?: number | null;
  unidade_preco?: string | null;
  escopo?: {
    qtd: number | null;
    unidade: string;
    fonte: 'unidade_informada' | 'descricao' | 'objeto' | 'edital';
  } | null;
  valor_global?: number | null;
  edital?: {
    disponivel: boolean;
    objeto?: string | null;
    item_edital?: string | null;
    trecho?: string | null;
    numero?: string | null;
    processo?: string | null;
  } | null;
  confirmado_pelo_edital?: boolean;
  edital_resumo?: string | null;
}

export interface BiddingResultItem {
  id: number;
  item_numero?: string | null;
  fornecedor?: string | null;
  cnpj?: string | number | null;
  item_descricao?: string | null;
  item_descricao_detalhado?: string | null;
  item_unidade?: string | null;
  item_quantidade?: number | null;
  item_valor_unitario?: number | null;
  item_valor_total?: number | null;
  marca_item_ofertado?: string | null;
  fabricante_item_ofertado?: string | null;
  modelo_item_ofertado?: string | null;
}

export interface BiddingDatas {
  datahora_abertura?: string | null;
  datahora_prazo?: string | null;
  datahora_documento?: string | null;
  datahora_retirada?: string | null;
  datahora_visita?: string | null;
}

export interface BiddingFollowup {
  id: number;
  data_fonte?: string | null;
  sintese?: string | null;
}

export interface BiddingResult {
  licitacao: {
    id: number;
    objeto?: string | null;
    situacao?: string | null;
    edital?: string | null;
    processo?: string | null;
    observacao?: string | null;
    edital_status_id?: number | null;
    edicts?: Array<{ url: string; filename: string }> | null;
    datas?: BiddingDatas | null;
  };
  orgao?: {
    nome?: string | null;
    uasg?: string | null;
    cidade_uf?: string | null;
    endereco?: string | null;
    site?: string | null;
  } | null;
  resultados?: BiddingResultItem[] | null;
  acompanhamentos?: BiddingFollowup[] | null;
}

export interface OrgaoPublico {
  nome: string | null;
  uasg: string | null;
  endereco: string | null;
  cidade: string | null;
  uf: string | null;
  site: string | null;
}

export interface PriceStats {
  n: number;
  n_total: number;
  n_outliers?: number;
  media?: number;
  mediana?: number;
  min?: number;
  max?: number;
  p25?: number;
  p75?: number;
  desvio?: number;
  fornecedores_unicos: number;
  orgaos_unicos: number;
}

export interface TimeseriesPoint {
  month: string;
  mediana: number;
  p25: number;
  p75: number;
  n: number;
}

export interface Timeseries {
  points: TimeseriesPoint[];
  trend_pct: number | null;
}

export interface SearchAlert {
  tipo: string;
  tom: 'warning' | 'info';
  titulo: string;
  mensagem: string;
}

export interface UnitGroupStats {
  n: number;
  n_outliers?: number;
  media?: number;
  mediana?: number;
  min?: number;
  max?: number;
  p25?: number;
  p75?: number;
  desvio?: number;
  derivadas?: number;
  escopo_similar?: {
    n: number;
    mediana?: number;
    p25?: number;
    p75?: number;
    alvo_qtd: number;
    faixa_qtd: number[];
  } | null;
}

export type Regime = 'produto' | 'unitario' | 'global';

export interface SearchResponse {
  query: string;
  stats: PriceStats;
  timeseries: Timeseries;
  operacao_counts: Record<string, number>;
  alerts: SearchAlert[];
  hits: SearchHit[];
  total: number;
  curated?: boolean;
  spec_assumida?: string | null;
  queries_utilizadas?: string[];
  curation?: {
    kept: number;
    analyzed: number;
    discarded_by_llm: number;
    reason: string;
  };
  tipo_query?: TipoQuery;
  regime?: Regime;
  unidade_referencia?: string | null;
  unit_stats?: Record<string, UnitGroupStats> | null;
  edital_enrichment?: {
    ativado: boolean;
    licitacoes_com_edital?: number;
    escopos_derivados_do_edital?: number;
    resgate?: { aprovados?: number } | null;
  } | null;
}

export interface SearchParams {
  query: string;
  dataGte?: string | null;
  dataLte?: string | null;
  ufs?: string[] | null;
  queries?: string[] | null;
  specAssumida?: string | null;
  tipoQuery?: TipoQuery | null;
}

export interface SavedSearchResumo {
  mediana: number;
  media: number;
  menor: number;
  n_amostras: number;
  total_encontradas: number;
  fornecedores: number;
  orgaos: number;
  unidade?: string | null;
  regime?: Regime;
}

export interface SavedSearchItem {
  numero: string | null;
  quantidade: number | null;
  unidade: string | null;
}

export interface SavedSearchSnapshot {
  resumo: SavedSearchResumo;
  cesta: SearchHit[];
  stats_busca?: PriceStats;
  curation?: SearchResponse['curation'];
  regime?: Regime;
  unidade_referencia?: string | null;
  item?: SavedSearchItem | null;
}

export interface SavedSearchListItem {
  id: number;
  name: string;
  query: string;
  period_months: number | null;
  date_from: string | null;
  date_to: string | null;
  ufs: string[] | null;
  notes: string | null;
  batch_name: string | null;
  item: SavedSearchItem | null;
  resumo: SavedSearchResumo | null;
  created_at: string;
  updated_at: string;
}

export interface SavedSearch extends SavedSearchListItem {
  snapshot: SavedSearchSnapshot;
}

export type CampoArquivo =
  | 'descricao'
  | 'quantidade'
  | 'unidade'
  | 'numero_item'
  | 'lote'
  | 'codigo_catalogo'
  | 'valor_unitario';

export interface ItemDoArquivo {
  numero: string | null;
  lote: string | null;
  descricao: string;
  quantidade: number | null;
  unidade: string | null;
  codigo_catalogo: string | null;
  valor_referencia: number | null;
  aba: string;
  linha: number;
  pagina?: number | null;
  alerta?: string | null;
}

export interface LinhaIgnorada {
  aba: string;
  linha: number;
  texto: string;
  tipo: 'total' | 'cabecalho' | 'sem_descricao' | 'titulo';
  motivo: string;
}

export interface ImportarItensResponse {
  arquivo: string;
  tipo?: 'planilha' | 'documento';
  documento?: {
    formato: 'pdf' | 'docx' | 'odt';
    paginas: number | null;
    paginas_lidas: number | null;
    paginas_sem_texto: number;
    repetidos: number;
    alertas: number;
    lacunas: number[][];
  };
  itens: ItemDoArquivo[];
  total_itens: number;
  abas: Array<{
    nome: string;
    itens: number;
    origem: 'ia' | 'heuristica';
    colunas: Array<{ campo: CampoArquivo; coluna: string; titulo: string | null }>;
  }>;
  ignoradas: LinhaIgnorada[];
  total_ignoradas: number;
  truncado: boolean;
  leitura_incompleta?: boolean;
  limite_itens: number;
  mensagem: string | null;
}

export const criarCancelamento = (): CancelTokenSource => axios.CancelToken.source();

export const foiCancelada = (error: unknown): boolean => axios.isCancel(error);

export const priceIntelligenceApi = {
  async health(): Promise<{ points_count?: number }> {
    const { data } = await Api.get('/price_intelligence/health', { timeout: 10000 });
    return data || {};
  },

  async analyze(query: string, cancel?: CancelTokenSource): Promise<AnalisarResponse> {
    const { data } = await Api.post<AnalisarResponse>(
      '/price_intelligence/analyze',
      { query: query.slice(0, ANALISE_QUERY_MAX) },
      { timeout: ANALYZE_TIMEOUT_MS, cancelToken: cancel ? cancel.token : undefined },
    );
    return data;
  },

  async search(params: SearchParams, cancel?: CancelTokenSource): Promise<SearchResponse> {
    const body: Record<string, unknown> = { query: params.query };
    if (params.dataGte) body.data_gte = params.dataGte;
    if (params.dataLte) body.data_lte = params.dataLte;
    if (params.ufs && params.ufs.length) body.ufs = params.ufs;
    if (params.queries && params.queries.length) body.queries = params.queries;
    if (params.specAssumida) body.spec_assumida = params.specAssumida;
    if (params.tipoQuery) body.tipo_query = params.tipoQuery;

    const { data } = await Api.post<SearchResponse>('/price_intelligence/search', body, {
      timeout: SEARCH_TIMEOUT_MS,
      cancelToken: cancel ? cancel.token : undefined,
    });
    return data;
  },

  async importItems(arquivo: File, cancel?: CancelTokenSource): Promise<ImportarItensResponse> {
    const formData = new FormData();
    formData.append('file', arquivo, arquivo.name);
    const { data } = await Api.post<ImportarItensResponse>(
      '/price_intelligence/import_items',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: IMPORT_TIMEOUT_MS,
        cancelToken: cancel ? cancel.token : undefined,
      },
    );
    return data;
  },

  async listSaved(q?: string): Promise<SavedSearchListItem[]> {
    const { data } = await Api.get('/price_intelligence/saved', { params: q ? { q } : {} });
    return (data && data.saved_searches) || [];
  },

  async getSaved(id: number | string): Promise<SavedSearch> {
    const { data } = await Api.get<SavedSearch>(`/price_intelligence/saved/${id}`);
    return data;
  },

  async createSaved(params: {
    name: string;
    query: string;
    periodMonths?: number | null;
    dateFrom?: string | null;
    dateTo?: string | null;
    ufs?: string[] | null;
    notes?: string | null;
    batchName?: string | null;
    snapshot: SavedSearchSnapshot;
  }): Promise<SavedSearch> {
    const { data } = await Api.post<SavedSearch>('/price_intelligence/saved', {
      name: params.name,
      query: params.query,
      period_months: params.periodMonths,
      date_from: params.dateFrom,
      date_to: params.dateTo,
      ufs: params.ufs,
      notes: params.notes,
      batch_name: params.batchName,
      snapshot: params.snapshot,
    });
    return data;
  },

  async updateSaved(
    id: number | string,
    patch: { name?: string; notes?: string | null; batch_name?: string | null },
  ): Promise<SavedSearch> {
    const { data } = await Api.patch<SavedSearch>(`/price_intelligence/saved/${id}`, patch);
    return data;
  },

  async removeSaved(id: number | string): Promise<void> {
    await Api.delete(`/price_intelligence/saved/${id}`);
  },

  async getBidding(licitacaoId: number | string): Promise<BiddingResult | null> {
    const { data } = await Api.get('/mercado/get_result_bidding.json', {
      params: { bidding_id: licitacaoId },
    });
    return data && data.licitacao ? data : null;
  },

  async getPublicBody(orgaoId: number | string): Promise<OrgaoPublico | null> {
    const { data } = await Api.get('/mercado/get_public_body.json', {
      params: { public_body_id: orgaoId },
    });
    if (!data || !data.dados) return null;
    return {
      nome: data.dados.nome || null,
      uasg: data.dados.uasg || null,
      endereco: data.dados.endereco || null,
      cidade: data.cidade || null,
      uf: data.uf || null,
      site: data.dados.edital_site || null,
    };
  },
};

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const inteiro = new Intl.NumberFormat('pt-BR');

export const fmtBRL = (v: number): string => brl.format(v);

export const fmtInt = (v: number): string => inteiro.format(v);

export const fmtBRLCompact = (v: number): string => {
  const abs = Math.abs(v);
  if (abs >= 1000000)
    return `R$ ${(v / 1000000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
  if (abs >= 10000)
    return `R$ ${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`;
  return brl.format(v);
};

export const fmtDataBR = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = iso.slice(0, 10).split('-');
  return d.length === 3 ? `${d[2]}/${d[1]}/${d[0]}` : iso;
};

const MESES_CURTOS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];

export const fmtMes = (month: string): string => {
  const partes = month.split('-');
  const idx = Number(partes[1]) - 1;
  return `${MESES_CURTOS[idx] || partes[1]}/${(partes[0] || '').slice(2)}`;
};

export const dataGteParaMeses = (meses: number): string | null => {
  if (meses <= 0) return null;
  const d = new Date();
  d.setMonth(d.getMonth() - meses);
  return d.toISOString().slice(0, 10);
};

export const PERIODO_PADRAO = 6;
export const PERIODO_PERSONALIZADO = -1;

export interface FiltrosPesquisa {
  periodoMeses: number;
  dataDe: string;
  dataAte: string;
  ufs: string[];
}

export const parametrosDosFiltros = (
  f: FiltrosPesquisa,
): { ufs: string[] | null; dataGte: string | null; dataLte: string | null } => {
  const personalizado = f.periodoMeses === PERIODO_PERSONALIZADO;
  return {
    ufs: f.ufs.length ? f.ufs : null,
    dataGte: personalizado ? f.dataDe || null : dataGteParaMeses(f.periodoMeses),
    dataLte: personalizado ? f.dataAte || null : null,
  };
};

export const rotuloPeriodo = (f: Pick<FiltrosPesquisa, 'periodoMeses' | 'dataDe' | 'dataAte'>) =>
  f.periodoMeses === PERIODO_PERSONALIZADO
    ? `${f.dataDe ? fmtDataBR(f.dataDe) : 'início da base'} – ${
        f.dataAte ? fmtDataBR(f.dataAte) : 'hoje'
      }`
    : `${f.periodoMeses} meses`;

export const linkRefazerBusca = (saved: SavedSearchListItem): string => {
  const params = new URLSearchParams({ q: saved.query });
  if (saved.date_from || saved.date_to) {
    params.set('periodo', String(PERIODO_PERSONALIZADO));
    if (saved.date_from) params.set('de', saved.date_from.slice(0, 10));
    if (saved.date_to) params.set('ate', saved.date_to.slice(0, 10));
  } else if (saved.period_months) {
    params.set('periodo', String(saved.period_months));
  }
  if (saved.ufs && saved.ufs.length) params.set('ufs', saved.ufs.join(','));
  return `/pesquisa_de_precos?${params.toString()}`;
};

export const mediana = (values: number[]): number | null => {
  if (!values.length) return null;
  const s = values.slice().sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};

export const percentil = (values: number[], pct: number): number | null => {
  if (!values.length) return null;
  const s = values.slice().sort((a, b) => a - b);
  const k = ((s.length - 1) * pct) / 100;
  const f = Math.floor(k);
  const c = Math.min(f + 1, s.length - 1);
  if (f === c) return s[f];
  return s[f] + (s[c] - s[f]) * (k - f);
};

export const valorUnitarioDe = (hit: SearchHit): number | null => {
  const v = hit.payload.item_valor_unitario;
  return typeof v === 'number' && v > 0 ? v : null;
};

export const valorComparavelDe = (
  r: Pick<SearchResponse, 'regime' | 'unidade_referencia'>,
  hit: SearchHit,
): number | null => {
  if (r.regime === 'unitario') {
    return hit.unidade_preco === (r.unidade_referencia || null) &&
      typeof hit.preco_normalizado === 'number' &&
      hit.preco_normalizado > 0
      ? hit.preco_normalizado
      : null;
  }
  if (r.regime === 'global') {
    const v = hit.valor_global || hit.payload.item_valor_total;
    return typeof v === 'number' && v > 0 ? v : null;
  }
  return valorUnitarioDe(hit);
};

export const serieMensal = (
  hits: SearchHit[],
  valorDe: (h: SearchHit) => number | null,
): Timeseries => {
  const buckets: Record<string, number[]> = {};
  hits.forEach((h) => {
    const v = valorDe(h);
    const month = String(h.payload.created || '').slice(0, 7);
    if (!v || month.length !== 7 || month[4] !== '-') return;
    if (!buckets[month]) buckets[month] = [];
    buckets[month].push(v);
  });
  const points = Object.keys(buckets)
    .sort()
    .map((month) => ({
      month,
      mediana: mediana(buckets[month]) || 0,
      p25: percentil(buckets[month], 25) || 0,
      p75: percentil(buckets[month], 75) || 0,
      n: buckets[month].length,
    }));
  let trend: number | null = null;
  if (points.length >= 3) {
    const mid = Math.floor(points.length / 2);
    const antes = mediana(
      points
        .slice(0, mid)
        .filter((p) => p.n >= 2)
        .map((p) => p.mediana),
    );
    const depois = mediana(
      points
        .slice(mid)
        .filter((p) => p.n >= 2)
        .map((p) => p.mediana),
    );
    if (antes && depois) trend = Math.round(((depois - antes) / antes) * 1000) / 10;
  }
  return { points, trend_pct: trend };
};

export interface ReferenciaDePreco {
  n: number;
  mediana: number | null;
  media: number | null;
  menor: number | null;
  p25: number | null;
  p75: number | null;
  fornecedores: number;
  orgaos: number;
}

export const referenciaDaCesta = (
  cesta: SearchHit[],
  valorDe: (h: SearchHit) => number | null,
): ReferenciaDePreco => {
  const valores: number[] = [];
  const fornecedores: Record<string, boolean> = {};
  const orgaos: Record<string, boolean> = {};
  cesta.forEach((h) => {
    const v = valorDe(h);
    if (v !== null) valores.push(v);
    const f = h.payload.cnpj || h.payload.fornecedor;
    if (f) fornecedores[f] = true;
    const o = h.payload.orgao_nome;
    if (o) orgaos[o] = true;
  });
  const soma = valores.reduce((s, v) => s + v, 0);
  return {
    n: valores.length,
    mediana: mediana(valores),
    media: valores.length ? soma / valores.length : null,
    menor: valores.length ? Math.min.apply(null, valores) : null,
    p25: percentil(valores, 25),
    p75: percentil(valores, 75),
    fornecedores: Object.keys(fornecedores).length,
    orgaos: Object.keys(orgaos).length,
  };
};

export const referenciaDoResultado = (r: SearchResponse): ReferenciaDePreco => {
  const daCesta = referenciaDaCesta(r.hits, (h) => valorComparavelDe(r, h));
  if (r.regime === 'global') return daCesta;
  const grupo =
    r.regime === 'unitario' && r.unidade_referencia && r.unit_stats
      ? r.unit_stats[r.unidade_referencia]
      : null;
  const base = grupo || r.stats;
  if (!base || typeof base.mediana !== 'number') return daCesta;
  return {
    ...daCesta,
    n: base.n,
    mediana: base.mediana,
    media: typeof base.media === 'number' ? base.media : daCesta.media,
    menor: typeof base.min === 'number' ? base.min : daCesta.menor,
    p25: typeof base.p25 === 'number' ? base.p25 : daCesta.p25,
    p75: typeof base.p75 === 'number' ? base.p75 : daCesta.p75,
  };
};

export const snapshotDoResultado = (
  r: SearchResponse,
  cesta: SearchHit[] = r.hits,
  referencia: ReferenciaDePreco = referenciaDoResultado(r),
): SavedSearchSnapshot => {
  const unidade = r.regime === 'unitario' ? r.unidade_referencia || null : null;
  return {
    resumo: {
      mediana: referencia.mediana || 0,
      media: referencia.media || 0,
      menor: referencia.menor || 0,
      n_amostras: cesta.length,
      total_encontradas: (r.stats && r.stats.n_total) || r.total,
      fornecedores: referencia.fornecedores,
      orgaos: referencia.orgaos,
      unidade,
      regime: r.regime || 'produto',
    },
    cesta,
    stats_busca: r.stats,
    curation: r.curation,
    regime: r.regime || 'produto',
    unidade_referencia: unidade,
  };
};

export interface CompetidorAgregado {
  nome: string;
  cnpj: string | null;
  vitorias: number;
  mediana: number | null;
  menor: number | null;
}

export const topFornecedores = (
  hits: SearchHit[],
  valorDe: (h: SearchHit) => number | null = valorUnitarioDe,
  limite = 5,
): CompetidorAgregado[] => {
  const porFornecedor: Record<
    string,
    { nome: string; cnpj: string | null; vitorias: number; valores: number[] }
  > = {};

  hits.forEach((hit) => {
    const nome = (hit.payload.fornecedor || '').trim();
    if (!nome) return;
    const chave = (hit.payload.cnpj || nome).trim();
    if (!porFornecedor[chave]) {
      porFornecedor[chave] = { nome, cnpj: hit.payload.cnpj || null, vitorias: 0, valores: [] };
    }
    porFornecedor[chave].vitorias += 1;
    const valor = valorDe(hit);
    if (valor !== null) porFornecedor[chave].valores.push(valor);
  });

  return Object.keys(porFornecedor)
    .map((chave) => {
      const f = porFornecedor[chave];
      return {
        nome: f.nome,
        cnpj: f.cnpj,
        vitorias: f.vitorias,
        mediana: mediana(f.valores),
        menor: f.valores.length ? Math.min.apply(null, f.valores) : null,
      };
    })
    .sort((a, b) => b.vitorias - a.vitorias)
    .slice(0, limite);
};

export interface CompradorAgregado {
  nome: string;
  uf: string | null;
  municipio: string | null;
  orgaoId: number | null;
  compras: number;
  mediana: number | null;
}

export const chaveDoOrgao = (p: SearchHitPayload): string | null => {
  if (p.orgao_id) return `id:${p.orgao_id}`;
  const nome = (p.orgao_nome || '').trim();
  return nome ? `nome:${nome}` : null;
};

export const topOrgaos = (
  hits: SearchHit[],
  valorDe: (h: SearchHit) => number | null = valorUnitarioDe,
  limite = 5,
): CompradorAgregado[] => {
  const porOrgao: Record<string, CompradorAgregado & { valores: number[] }> = {};
  const ordem: string[] = [];

  hits.forEach((hit) => {
    const chave = chaveDoOrgao(hit.payload);
    if (!chave) return;
    if (!porOrgao[chave]) {
      porOrgao[chave] = {
        nome: (hit.payload.orgao_nome || '').trim() || `Órgão ${hit.payload.orgao_id}`,
        uf: hit.payload.uf || null,
        municipio: hit.payload.municipio || null,
        orgaoId: hit.payload.orgao_id || null,
        compras: 0,
        mediana: null,
        valores: [],
      };
      ordem.push(chave);
    }
    porOrgao[chave].compras += 1;
    const valor = valorDe(hit);
    if (valor !== null) porOrgao[chave].valores.push(valor);
  });

  return ordem
    .map((chave) => {
      const { valores, ...orgao } = porOrgao[chave];
      return { ...orgao, mediana: mediana(valores) };
    })
    .sort((a, b) => b.compras - a.compras)
    .slice(0, limite);
};

export interface FiltrosAmostras {
  descricao: string;
  orgao: string;
  municipio: string;
  fornecedor: string;
  marca: string;
  valorMin: string;
  valorMax: string;
}

export const FILTROS_AMOSTRAS_VAZIOS: FiltrosAmostras = {
  descricao: '',
  orgao: '',
  municipio: '',
  fornecedor: '',
  marca: '',
  valorMin: '',
  valorMax: '',
};

export const totalFiltrosAmostras = (f: FiltrosAmostras): number =>
  (Object.keys(f) as Array<keyof FiltrosAmostras>).filter((k) => f[k].trim() !== '').length;

const semAcento = (texto?: string | null): string =>
  (texto || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const contem = (campo: string | null | undefined, termo: string): boolean =>
  semAcento(campo).indexOf(semAcento(termo).trim()) >= 0;

const numeroDoFiltro = (valor: string): number | null => {
  const n = parseFloat(valor.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

// CNPJ pode ser alfanumérico: só os caracteres fora de [0-9A-Z] são máscara.
const cnpjLimpo = (cnpj?: string | number | null): string =>
  String(cnpj || '')
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '');

export const filtrarAmostras = (hits: SearchHit[], f: FiltrosAmostras): SearchHit[] => {
  if (!totalFiltrosAmostras(f)) return hits;
  const min = numeroDoFiltro(f.valorMin);
  const max = numeroDoFiltro(f.valorMax);
  const termoCnpj = /\d/.test(f.fornecedor) ? cnpjLimpo(f.fornecedor) : '';

  return hits.filter((h) => {
    const p = h.payload;
    if (
      f.descricao.trim() &&
      !contem(`${p.item_descricao || ''} ${p.item_descricao_detalhado || ''}`, f.descricao)
    )
      return false;
    if (f.orgao.trim() && !contem(p.orgao_nome, f.orgao)) return false;
    if (
      f.municipio.trim() &&
      !contem(p.municipio, f.municipio) &&
      !contem(p.orgao_nome, f.municipio)
    )
      return false;
    if (
      f.fornecedor.trim() &&
      !contem(p.fornecedor, f.fornecedor) &&
      !(termoCnpj && cnpjLimpo(p.cnpj).indexOf(termoCnpj) >= 0)
    )
      return false;
    if (f.marca.trim() && !contem([p.marca, p.fabricante, p.modelo].join(' '), f.marca))
      return false;
    const valor = p.item_valor_unitario;
    if (min !== null && (typeof valor !== 'number' || valor < min)) return false;
    if (max !== null && (typeof valor !== 'number' || valor > max)) return false;
    return true;
  });
};

export interface LicitacaoAgrupada {
  licitacaoId: number;
  objeto: string | null;
  orgaoNome: string | null;
  uf: string | null;
  municipio: string | null;
  data: string | null;
  fornecedores: string[];
  itens: SearchHit[];
  total: number | null;
}

export const agruparPorLicitacao = (hits: SearchHit[]): LicitacaoAgrupada[] => {
  const ordem: number[] = [];
  const grupos: Record<number, LicitacaoAgrupada> = {};
  hits.forEach((h) => {
    const id = h.payload.licitacao_id;
    if (!id) return;
    if (!grupos[id]) {
      grupos[id] = {
        licitacaoId: id,
        objeto: h.payload.objeto || null,
        orgaoNome: h.payload.orgao_nome || null,
        uf: h.payload.uf || null,
        municipio: h.payload.municipio || null,
        data: h.payload.created || null,
        fornecedores: [],
        itens: [],
        total: null,
      };
      ordem.push(id);
    }
    const grupo = grupos[id];
    grupo.itens.push(h);
    const fornecedor = (h.payload.fornecedor || '').trim();
    if (fornecedor && grupo.fornecedores.indexOf(fornecedor) < 0)
      grupo.fornecedores.push(fornecedor);
    const total = h.payload.item_valor_total;
    if (typeof total === 'number' && total > 0) grupo.total = (grupo.total || 0) + total;
  });
  return ordem.map((id) => grupos[id]);
};

export interface VolumePoint {
  month: string;
  n: number;
}

const mesSeguinte = (month: string): string => {
  const ano = Number(month.slice(0, 4));
  const mes = Number(month.slice(5, 7));
  const d = new Date(ano, mes, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export const volumeMensal = (hits: SearchHit[]): VolumePoint[] => {
  const contagem: Record<string, number> = {};
  hits.forEach((h) => {
    const month = String(h.payload.created || '').slice(0, 7);
    if (month.length !== 7 || month[4] !== '-') return;
    contagem[month] = (contagem[month] || 0) + 1;
  });
  const meses = Object.keys(contagem).sort();
  if (!meses.length) return [];
  const pontos: VolumePoint[] = [];
  let atual = meses[0];
  const ultimo = meses[meses.length - 1];
  while (atual <= ultimo && pontos.length < 60) {
    pontos.push({ month: atual, n: contagem[atual] || 0 });
    atual = mesSeguinte(atual);
  }
  return pontos;
};

const ALIAS_UNIDADES: Record<string, string[]> = {
  'M²': ['m2', 'm²', 'metros?\\s+quadrados?'],
  'M³': ['m3', 'm³', 'metros?\\s+c[uú]bicos?'],
  KM: ['km', 'quil[oô]metros?'],
  METRO: ['metros?', 'm(?![²³\\w])'],
  HORA: ['horas?', 'hrs?\\b'],
  MÊS: ['m[eê]s(?:es)?'],
  DIA: ['di[aá]rias?', 'dias?'],
  L: ['litros?'],
  KG: ['kg', 'quilos?'],
  TON: ['toneladas?', 'ton\\b'],
};

export const extrairQuantidadeDoTexto = (texto: string, unidade: string | null): number | null => {
  if (!texto) return null;
  const canonica = unidadeCanonica(unidade);
  if (canonica && !ALIAS_UNIDADES[canonica]) return null;
  const padroes = canonica
    ? ALIAS_UNIDADES[canonica]
    : Object.keys(ALIAS_UNIDADES).reduce((acc: string[], u) => acc.concat(ALIAS_UNIDADES[u]), []);
  const re = new RegExp(
    `(\\d{1,3}(?:\\.\\d{3})+(?:,\\d+)?|\\d+(?:[.,]\\d+)?)\\s*(?:${padroes.join('|')})`,
    'i',
  );
  const m = texto.match(re);
  if (!m) return null;
  const milhar = /^\d{1,3}(\.\d{3})+(,\d+)?$/.test(m[1]);
  const numero = milhar ? m[1].replace(/\./g, '').replace(',', '.') : m[1].replace(',', '.');
  const v = parseFloat(numero);
  return Number.isFinite(v) && v > 0 ? v : null;
};

const cortarEmPalavra = (texto: string, limite: number): string => {
  if (limite <= 0) return '';
  if (texto.length <= limite) return texto;
  const bruto = texto.slice(0, limite);
  const fronteira = bruto.lastIndexOf(' ');
  return (fronteira > 0 ? bruto.slice(0, fronteira) : bruto).trim();
};

export const textoParaAnalise = (base: string, refino = ''): string => {
  const baseLimpa = base.replace(/\s+/g, ' ').trim();
  const refinoLimpo = refino.replace(/\s+/g, ' ').trim();
  if (!refinoLimpo) return cortarEmPalavra(baseLimpa, ANALISE_QUERY_MAX);
  const refinoFinal = cortarEmPalavra(refinoLimpo, ANALISE_QUERY_MAX);
  const sobra = ANALISE_QUERY_MAX - refinoFinal.length - 1;
  const baseFinal = sobra >= 24 ? cortarEmPalavra(baseLimpa, sobra) : '';
  return `${baseFinal} ${refinoFinal}`.trim();
};

export const linkLicitacao = (licitacaoId?: number | null): string | null =>
  licitacaoId ? `${window.location.origin}/pesquisa_de_precos/licitacao/${licitacaoId}` : null;

export const baixarArquivo = (conteudo: string, nomeArquivo: string, tipo: string): void => {
  const blob = new Blob([conteudo], { type: tipo });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeArquivo;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const exportarCsv = (hits: SearchHit[], nomeArquivo: string): void => {
  const cab = [
    'descricao',
    'marca',
    'modelo',
    'orgao',
    'municipio',
    'uf',
    'fornecedor',
    'cnpj',
    'unidade',
    'quantidade',
    'valor_unitario',
    'valor_total',
    'preco_normalizado',
    'unidade_do_preco_normalizado',
    'data',
    'referencia_edital',
    'licitacao_id',
    'link_conlicitacao',
  ];

  const esc = (v: unknown): string => {
    const s = v === null || v === undefined ? '' : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };

  const num = (v: unknown): string =>
    typeof v === 'number' ? esc(String(v).replace('.', ',')) : esc(v);

  const linhas = hits.map((h) =>
    [
      esc(h.payload.item_descricao),
      esc([h.payload.marca, h.payload.fabricante].filter(Boolean).join(' / ')),
      esc(h.payload.modelo),
      esc(h.payload.orgao_nome),
      esc(h.payload.municipio),
      esc(h.payload.uf),
      esc(h.payload.fornecedor),
      esc(h.payload.cnpj),
      esc(h.payload.item_unidade),
      num(h.payload.item_quantidade),
      num(h.payload.item_valor_unitario),
      num(h.payload.item_valor_total),
      num(h.preco_normalizado),
      esc(h.unidade_preco),
      esc((h.payload.created || '').slice(0, 10)),
      esc(h.edital ? h.edital.item_edital : ''),
      esc(h.payload.licitacao_id),
      esc(linkLicitacao(h.payload.licitacao_id) || ''),
    ].join(';'),
  );

  // BOM UTF-8 para o Excel abrir acentos corretamente.
  baixarArquivo(
    `\uFEFF${cab.join(';')}\n${linhas.join('\n')}`,
    `${nomeArquivo}.csv`,
    'text/csv;charset=utf-8',
  );
};
